import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageCircle, ArrowRight, Star, Home, Building2, HardHat, Sparkles, Smartphone, ClipboardCheck, Users, DollarSign, Clock, Zap, Check, Calendar, Mail, Phone, MapPin, User } from 'lucide-react';
import { services, whyChooseUs, testimonials, contactInfo } from '../data/mock';

const iconMap = {
  Home,
  Building2,
  HardHat,
  Sparkles,
  Smartphone,
  ClipboardCheck,
  Users,
  DollarSign,
  Clock
};

// WhatsApp SVG Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    service: '',
    date: '',
    time: '',
    phone: '',
    email: '',
    address: ''
  });

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Google Ads Conversion Tracking
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17761693801/KUPZCNyatccbEOngt5VC',
        'value': 1.0,
        'currency': 'TRY'
      });
    }
    
    // WhatsApp mesajı formatla
    const serviceTitle = formData.service || 'Genel Teklif Talebi';
    const message = `🏠 *${serviceTitle} - Teklif Talebi*\n\n` +
      `👤 *Ad Soyad:* ${formData.name} ${formData.surname}\n` +
      `🧹 *Hizmet:* ${formData.service}\n` +
      `📅 *Tarih:* ${formData.date}\n` +
      `⏰ *Saat:* ${formData.time}\n` +
      `📞 *Telefon:* ${formData.phone}\n` +
      `📧 *E-posta:* ${formData.email}\n` +
      `📍 *Adres:* ${formData.address}`;
    
    const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    // Formu temizle
    setFormData({
      name: '',
      surname: '',
      service: '',
      date: '',
      time: '',
      phone: '',
      email: '',
      address: ''
    });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)' }}>
      {/* Futuristic Neon Grid Background */}
      <div className="neon-grid"></div>
      <div className="neon-beams"></div>
      
      {/* Global Wavy Lines */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 wavy-lines-dark opacity-40"></div>
        <div className="absolute inset-0 flow-lines-dark opacity-30"></div>
        <div className="absolute inset-0 diagonal-lines opacity-20"></div>
      </div>

      {/* Hero Section - EPIC C&D CLEAN Title */}
      <section id="hero" className="hero-section relative pt-40 pb-16 px-4 overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 fade-in">
              <div className="inline-block px-5 py-2 neon-card rounded-full text-sm font-bold animate-pulse">
                <span className="text-neon-blue flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Dijital Temizlik Çözümleri
                </span>
              </div>
              
              {/* MEGA EPIC TITLE - BEYAZ */}
              <div className="relative">
                {/* Glow Behind Text */}
                <div className="absolute -inset-4 bg-gradient-to-r from-parliament-blue via-neon-blue to-parliament-blue blur-3xl opacity-30 animate-pulse"></div>
                <h1 className="text-7xl md:text-8xl lg:text-9xl font-black leading-tight relative text-white" style={{
                  textShadow: '0 0 40px rgba(0, 212, 255, 0.6), 0 0 80px rgba(37, 84, 184, 0.4)',
                  filter: 'drop-shadow(0 0 20px rgba(0, 212, 255, 0.6)) drop-shadow(0 0 40px rgba(37, 84, 184, 0.4))'
                }}>
                  C&D CLEAN
                </h1>
                {/* Animated Border */}
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-blue to-transparent" style={{
                  boxShadow: '0 0 20px rgba(0, 212, 255, 0.8)',
                  animation: 'shine 2s infinite'
                }}></div>
              </div>
              
              <p className="text-2xl md:text-3xl font-bold text-neon-blue shine-effect">
                Profesyonel Temizlikte Dijital Güvenceniz
              </p>
              <p className="text-lg font-semibold text-gray-300">
                Ev Temizliği • Ofis Temizliği • İnşaat Sonrası • Cam Temizliği
              </p>
              
              {/* WhatsApp Button - YEŞİL */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button 
                  size="lg" 
                  onClick={handleWhatsApp}
                  className="bg-gradient-to-r from-green-500 via-green-600 to-green-500 hover:from-green-600 hover:to-green-700 text-white font-black text-lg px-10 py-6 group shadow-2xl relative overflow-hidden"
                  style={{
                    boxShadow: '0 0 25px rgba(34, 197, 94, 0.6), 0 0 50px rgba(34, 197, 94, 0.4)'
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-green-600 via-green-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
                  <WhatsAppIcon className="mr-2 w-6 h-6 relative z-10" />
                  <span className="relative z-10">WhatsApp'tan Mesaj At</span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform relative z-10" />
                </Button>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-12 h-12 rounded-full neon-card flex items-center justify-center">
                      <span className="text-lg">⭐</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-white font-bold">500+ Mutlu Müşteri</p>
                  <p className="text-neon-blue text-sm">5.0 Mükemmel Puan</p>
                </div>
              </div>
            </div>
            <div className="relative lg:block hidden">
              <div className="absolute -inset-4 bg-gradient-to-r from-parliament-blue to-neon-blue rounded-3xl blur-xl opacity-30 animate-pulse"></div>
              <img
                src="https://images.unsplash.com/photo-1742483359033-13315b247c74"
                alt="Professional Cleaning"
                fetchpriority="high"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover relative z-10 neon-card"
                style={{ maxHeight: '450px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 neon-card rounded-full text-sm font-semibold mb-3">
              <span className="text-neon-blue">HİZMETLERİMİZ</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-black gradient-text mb-3">
              Profesyonel Temizlik Çözümleri
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Modern ekipman ve uzman kadromuzla her türlü temizlik ihtiyacınıza özel çözümler sunuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              
              return (
                <Link
                  key={service.id}
                  to={`/hizmetler/${service.id}`}
                  className="block h-full"
                >
                  <Card className="service-card h-full cursor-pointer group neon-card">
                  <CardContent className="p-0">
                    <div className="relative overflow-hidden rounded-t-xl h-48">
                      <img
                        src={service.image}
                        alt={service.title}
                        fetchpriority="high"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <div className="w-12 h-12 bg-neon-blue/20 backdrop-blur-md rounded-xl flex items-center justify-center group-hover:bg-neon-blue/40 transition-colors border border-neon-blue/50">
                          {Icon && <Icon className="w-6 h-6 text-neon-blue" />}
                        </div>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-neon-blue mb-2 group-hover:text-white transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">{service.shortDesc}</p>
                      <div className="flex items-center text-neon-blue font-semibold text-sm group-hover:gap-2 transition-all">
                        Detaylı İncele
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Symmetric 3+2 Layout */}
      <section id="about" className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 neon-card rounded-full text-sm font-semibold mb-3">
              <span className="text-neon-blue">NEDEN BİZ?</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-black gradient-text mb-3">
              C&D Clean Farkı
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Dijital teknoloji ve profesyonel hizmet anlayışıyla sektörde öncü konumdayız.
            </p>
          </div>

          {/* First Row - 3 items */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {whyChooseUs.slice(0, 3).map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card key={index} className="hover:scale-105 transition-all duration-300 neon-card">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-neon-blue/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 border border-neon-blue/40">
                      {Icon && <Icon className="w-7 h-7 text-neon-blue" />}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Second Row - 2 items centered */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whyChooseUs.slice(3, 5).map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card key={index + 3} className="hover:scale-105 transition-all duration-300 neon-card">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-neon-blue/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 border border-neon-blue/40">
                      {Icon && <Icon className="w-7 h-7 text-neon-blue" />}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Fixed Colors */}
      <section className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 neon-card rounded-full text-sm font-semibold mb-3">
              <span className="text-neon-blue">MÜŞTERİ YORUMLARI</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-black gradient-text mb-3">
              Mutlu Müşterilerimiz
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Memnuniyetiniz bizim en büyük motivasyonumuz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card neon-card">
                <CardContent className="p-6 relative z-10">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current text-neon-blue" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic text-base leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-neon-blue/30 pt-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-neon-blue/20 rounded-full flex items-center justify-center border border-neon-blue/40">
                      <span className="text-neon-blue font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm">{testimonial.name}</p>
                      <p className="text-xs text-neon-blue">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - INLINE FORM */}
      <section id="contact-form" className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Başlık */}
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold mb-4 border border-green-500/40">
                HEMEN BAŞLAYIN
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
                Ücretsiz Teklif Alın
              </h2>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                Bilgilerinizi doldurun, size en kısa sürede dönelim!
              </p>
            </div>

            {/* Inline Form */}
            <div className="neon-card p-6 md:p-8 rounded-2xl border-2 border-green-500/30" style={{
              boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)'
            }}>
              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* İsim ve Soyisim */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cta-name" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-green-400" />
                      İsim *
                    </Label>
                    <Input
                      id="cta-name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      placeholder="Adınız"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cta-surname" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-green-400" />
                      Soyisim *
                    </Label>
                    <Input
                      id="cta-surname"
                      name="surname"
                      value={formData.surname}
                      onChange={handleFormChange}
                      required
                      placeholder="Soyadınız"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                </div>

                {/* Hizmet Seçimi */}
                <div className="space-y-2">
                  <Label htmlFor="cta-service" className="text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-green-400" />
                    Hizmetinizi Seçin *
                  </Label>
                  <Select 
                    value={formData.service} 
                    onValueChange={(value) => setFormData({...formData, service: value})}
                    required
                  >
                    <SelectTrigger className="bg-gray-800/50 border-green-500/30 text-white focus:border-green-400">
                      <SelectValue placeholder="Bir hizmet seçin..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-green-500/30">
                      <SelectItem value="Ev Temizliği" className="text-white hover:bg-green-500/20">
                        Ev Temizliği
                      </SelectItem>
                      <SelectItem value="Ofis Temizliği" className="text-white hover:bg-green-500/20">
                        Ofis Temizliği
                      </SelectItem>
                      <SelectItem value="İnşaat Sonrası Temizlik" className="text-white hover:bg-green-500/20">
                        İnşaat Sonrası Temizlik
                      </SelectItem>
                      <SelectItem value="Cam Temizliği" className="text-white hover:bg-green-500/20">
                        Cam Temizliği
                      </SelectItem>
                      <SelectItem value="Gündelik Temizlikçi" className="text-white hover:bg-green-500/20">
                        Gündelik Temizlikçi
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tarih ve Saat */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cta-date" className="text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-green-400" />
                      Tarih *
                    </Label>
                    <Input
                      id="cta-date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleFormChange}
                      required
                      className="bg-gray-800/50 border-green-500/30 text-white focus:border-green-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cta-time" className="text-white flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-400" />
                      Saat *
                    </Label>
                    <Input
                      id="cta-time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleFormChange}
                      required
                      className="bg-gray-800/50 border-green-500/30 text-white focus:border-green-400"
                    />
                  </div>
                </div>

                {/* Telefon ve E-posta */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cta-phone" className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-400" />
                      Telefon Numarası *
                    </Label>
                    <Input
                      id="cta-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      placeholder="05XX XXX XX XX"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cta-email" className="text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-green-400" />
                      E-posta *
                    </Label>
                    <Input
                      id="cta-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      placeholder="ornek@email.com"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                </div>

                {/* Adres */}
                <div className="space-y-2">
                  <Label htmlFor="cta-address" className="text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-400" />
                    Adresiniz *
                  </Label>
                  <Textarea
                    id="cta-address"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    required
                    placeholder="Tam adresinizi yazınız..."
                    rows={3}
                    className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400 resize-none"
                  />
                </div>

                {/* Gönder Butonu */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-base md:text-lg py-6 group relative overflow-hidden"
                  style={{
                    boxShadow: '0 0 25px rgba(34, 197, 94, 0.6), 0 0 50px rgba(34, 197, 94, 0.4)'
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-green-600 via-green-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
                  <Zap className="mr-2 w-6 h-6 relative z-10" />
                  <span className="relative z-10">Şimdi Teklif Al !</span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform relative z-10" />
                </Button>
              </form>

              {/* Stats */}
              <div className="mt-8 pt-8 border-t border-green-500/20 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-black mb-1 text-green-400">500+</p>
                  <p className="text-gray-400 text-sm">Mutlu Müşteri</p>
                </div>
                <div className="hidden sm:block h-12 w-px bg-green-500/30"></div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-black mb-1 text-green-400">5.0</p>
                  <p className="text-gray-400 text-sm">Mükemmel Puan</p>
                </div>
                <div className="hidden sm:block h-12 w-px bg-green-500/30"></div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-black mb-1 text-green-400">24/7</p>
                  <p className="text-gray-400 text-sm">Destek</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;